import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError, switchMap, filter, take, tap } from 'rxjs/operators';
import { AuthCoreService } from '../../core/auth-core/auth-core.service';
import { ApiV1Service } from '../../core/api/apiv1.service';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  constructor(
    private apiV1: ApiV1Service,
    private authCoreService: AuthCoreService,
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const isOpenAI = request.url.includes('api.openai.com');
    const isRefreshCall = request.url.includes('/auth/local/refresh');

    if (AuthCoreService.isLoggedIn && !isOpenAI) {
      const userToken = AuthCoreService.token;
      request = request.clone({
        headers: request.headers.set('Authorization', `Bearer ${userToken}`),
      });
    }

    return next.handle(request).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 401 && !isOpenAI && !isRefreshCall) {
          return this.handle401Error(request, next);
        }
        return throwError(() => error);
      }),
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.apiV1
        .postWithCredentials<any>('/auth/local/refresh', {})
        .pipe(
          tap((response) => {
            const newToken = response?.token || response?.accessToken;
            if (newToken) {
              AuthCoreService.setToken(newToken);
              this.refreshTokenSubject.next(newToken);
            }
          }),
          switchMap(() => {
            const newToken = AuthCoreService.token;
            const cloned = request.clone({
              headers: request.headers.set('Authorization', `Bearer ${newToken}`),
            });
            return next.handle(cloned);
          }),
          catchError((err) => {
            this.authCoreService.forceLogout();
            return throwError(() => err);
          }),
          tap({
            next: () => {
              this.isRefreshing = false;
            },
            error: () => {
              this.isRefreshing = false;
            },
          }),
        );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((token) => {
          const cloned = request.clone({
            headers: request.headers.set('Authorization', `Bearer ${token}`),
          });
          return next.handle(cloned);
        }),
      );
    }
  }
}
